/*
 * Firefly purchase-target adapter.
 *
 * The native product variant remains the source of truth for option state,
 * availability and the master form selector.  This adapter only resolves the
 * server-rendered Firefly contract and prepares the request's first line item
 * when the contract explicitly maps that native variant to another target.
 */
(function (global) {
  'use strict';

  if (global.LumosFireflyPurchase) return;

  var hasWeakMap = typeof global.WeakMap === 'function';
  var stores = hasWeakMap ? new global.WeakMap() : [];
  var formOwners = hasWeakMap ? new global.WeakMap() : [];

  function each(items, callback) {
    if (!items) return;
    for (var index = 0; index < items.length; index += 1) callback(items[index], index);
  }

  function own(object, key) {
    return Object.prototype.hasOwnProperty.call(object, key);
  }

  function stringId(value) {
    if (value === undefined || value === null || value === '') return null;
    return String(value);
  }

  function sameId(left, right) {
    var leftId = stringId(left);
    var rightId = stringId(right);
    return leftId !== null && rightId !== null && leftId === rightId;
  }

  function finiteNumber(value) {
    var number = Number(value);
    return isFinite(number) ? number : null;
  }

  function positiveIntegerId(value) {
    var id = stringId(value);
    if (!id || !/^\d+$/.test(id) || Number(id) <= 0) return null;
    return Number(id);
  }

  function nonNegativeInteger(value) {
    if (typeof value !== 'number' || !isFinite(value) || value < 0 || Math.floor(value) !== value) return null;
    return value;
  }

  function clone(value) {
    if (value === undefined || value === null || typeof value !== 'object') return value;
    if (typeof global.structuredClone === 'function') {
      try { return global.structuredClone(value); } catch (error) { /* JSON fallback */ }
    }
    try { return JSON.parse(JSON.stringify(value)); } catch (error) { return null; }
  }

  function getState(owner) {
    if (!owner) return null;
    if (stores instanceof Array) {
      for (var index = 0; index < stores.length; index += 1) {
        if (stores[index].owner === owner) return stores[index].state;
      }
      return null;
    }
    return stores.get(owner) || null;
  }

  function setState(owner, state) {
    if (stores instanceof Array) {
      for (var index = 0; index < stores.length; index += 1) {
        if (stores[index].owner === owner) {
          stores[index].state = state;
          return;
        }
      }
      stores.push({ owner: owner, state: state });
      return;
    }
    stores.set(owner, state);
  }

  function deleteState(owner) {
    if (stores instanceof Array) {
      for (var index = stores.length - 1; index >= 0; index -= 1) {
        if (stores[index].owner === owner) stores.splice(index, 1);
      }
      return;
    }
    stores.delete(owner);
  }

  function getFormOwner(form) {
    if (!form) return null;
    if (formOwners instanceof Array) {
      for (var index = 0; index < formOwners.length; index += 1) {
        if (formOwners[index].form === form) return formOwners[index].owner;
      }
      return null;
    }
    return formOwners.get(form) || null;
  }

  function setFormOwner(form, owner) {
    if (!form || !owner) return;
    if (formOwners instanceof Array) {
      for (var index = 0; index < formOwners.length; index += 1) {
        if (formOwners[index].form === form) {
          formOwners[index].owner = owner;
          return;
        }
      }
      formOwners.push({ form: form, owner: owner });
      return;
    }
    formOwners.set(form, owner);
  }

  function deleteFormOwner(form, owner) {
    if (!form) return;
    if (formOwners instanceof Array) {
      for (var index = formOwners.length - 1; index >= 0; index -= 1) {
        if (formOwners[index].form === form && (!owner || formOwners[index].owner === owner)) formOwners.splice(index, 1);
      }
      return;
    }
    if (!owner || formOwners.get(form) === owner) formOwners.delete(form);
  }

  function ownerForm(owner, form) {
    if (form) return form;
    if (owner && owner._lumosCartForm) return owner._lumosCartForm;
    if (owner && owner.element && owner.element.querySelector) return owner.element.querySelector('form[action*="/cart/add"]');
    return null;
  }

  function contractNode(form) {
    if (!form || !form.querySelector) return null;
    return form.querySelector('script[data-lumos-firefly-contract]');
  }

  function readContract(form) {
    var node = contractNode(form);
    if (!node) return { present: false, value: undefined };
    var raw = node.textContent;
    if (raw === undefined || raw === null || raw === '') raw = node.innerHTML;
    if (raw === undefined || raw === null || String(raw).trim() === '') return { present: true, value: null };
    try {
      var parsed = JSON.parse(String(raw));
      return { present: true, value: parsed && typeof parsed === 'object' && !Array.isArray(parsed) ? parsed : null };
    } catch (error) {
      return { present: true, value: null };
    }
  }

  function productVariants(owner) {
    return owner && owner.productData && Array.isArray(owner.productData.variants) ? owner.productData.variants : null;
  }

  function variantForId(variants, id) {
    if (!variants) return null;
    for (var index = 0; index < variants.length; index += 1) {
      if (sameId(variants[index] && variants[index].id, id)) return variants[index];
    }
    return null;
  }

  function optionValue(variant, position) {
    if (!variant) return null;
    var key = 'option' + String(position);
    if (variant[key] !== undefined && variant[key] !== null) return String(variant[key]);
    if (Array.isArray(variant.options) && variant.options[position - 1] !== undefined) return String(variant.options[position - 1]);
    return null;
  }

  function normalizeTarget(target) {
    if (!target || typeof target !== 'object' || Array.isArray(target)) return null;
    var pack = stringId(target.pack);
    var id = positiveIntegerId(target.id);
    var nativeVariantId = positiveIntegerId(target.nativeVariantId);
    var mode = target.mode === 'native' || target.mode === 'external' ? target.mode : null;
    var price = nonNegativeInteger(target.price);
    var hasCompareAtPrice = target.compareAtPrice !== null && target.compareAtPrice !== undefined;
    var compareAtPrice = hasCompareAtPrice ? nonNegativeInteger(target.compareAtPrice) : null;
    if (!pack || !id || !nativeVariantId || !mode || typeof target.available !== 'boolean' || price === null || hasCompareAtPrice && compareAtPrice === null) return null;
    if (typeof target.priceText !== 'string' || target.priceText === '') return null;
    if (target.compareAtText !== null && target.compareAtText !== undefined && typeof target.compareAtText !== 'string') return null;
    return {
      id: id,
      nativeVariantId: nativeVariantId,
      available: target.available,
      price: price,
      compareAtPrice: compareAtPrice,
      priceText: target.priceText,
      compareAtText: target.compareAtText === undefined ? null : target.compareAtText,
      mode: mode,
      pack: pack
    };
  }

  function validateContract(owner, contract) {
    var variants = productVariants(owner);
    if (!contract || !owner || !owner.productData || !variants) return null;
    if (!own(contract, 'productId') || !positiveIntegerId(contract.productId) || !sameId(contract.productId, owner.productData.id)) return null;
    if (Number(contract.optionPosition) !== 2) return null;
    if (contract.mode !== 'native' && contract.mode !== 'external') return null;
    if (!Array.isArray(contract.targets) || contract.targets.length !== 3) return null;

    var seenPacks = {};
    var normalized = [];
    for (var index = 0; index < contract.targets.length; index += 1) {
      var target = normalizeTarget(contract.targets[index]);
      if (!target || (target.pack !== '1' && target.pack !== '2' && target.pack !== '4') || seenPacks[target.pack]) return null;
      seenPacks[target.pack] = true;
      var nativeVariant = variantForId(variants, target.nativeVariantId);
      if (!nativeVariant || optionValue(nativeVariant, contract.optionPosition) !== target.pack) return null;
      if (target.pack === '1' && target.mode !== 'native') return null;
      if (contract.mode === 'native' && target.mode !== 'native') return null;
      if (contract.mode === 'external' && target.pack !== '1' && target.mode !== 'external') return null;
      if (target.mode === 'native' && !sameId(target.id, target.nativeVariantId)) return null;
      if (target.mode === 'external' && sameId(target.id, target.nativeVariantId)) return null;
      normalized.push({ target: target, nativeVariant: nativeVariant });
    }
    if (!seenPacks['1'] || !seenPacks['2'] || !seenPacks['4']) return null;
    return normalized;
  }

  function resolve(owner, form, variant) {
    form = ownerForm(owner, form);
    var state = readContract(form);
    if (!state.present) return undefined;
    if (!state.value) return null;

    var validated = validateContract(owner, state.value);
    if (!validated) return null;
    var current = variant || (owner && owner.currentVariant);
    if (!current || (owner && owner.currentVariant && !sameId(owner.currentVariant.id, current.id))) return null;
    var currentNative = variantForId(productVariants(owner), current.id);
    if (!currentNative) return null;
    for (var index = 0; index < validated.length; index += 1) {
      var entry = validated[index];
      if (!sameId(entry.nativeVariant.id, currentNative.id)) continue;
      var target = entry.target;
      var result = {
        id: target.id,
        nativeVariantId: positiveIntegerId(currentNative.id),
        available: target.available === true && currentNative.available !== false,
        price: target.price,
        compareAtPrice: target.compareAtPrice,
        priceText: target.priceText,
        compareAtText: target.compareAtText,
        mode: target.mode,
        pack: target.pack
      };
      return result;
    }
    return null;
  }

  function requestItem(request) {
    if (!request || typeof request !== 'object') return null;
    if (Array.isArray(request.items)) return request.items.length ? request.items[0] : null;
    return request;
  }

  function prepareRequest(owner, form, request) {
    form = ownerForm(owner, form);
    var state = readContract(form);
    if (!state.present) return request;
    if (!state.value || !request || typeof request !== 'object') return null;
    var current = owner && owner.currentVariant;
    var target = resolve(owner, form, current);
    var item = requestItem(request);
    if (!target || target.available !== true || !item || !sameId(item.id, current && current.id)) return null;
    var prepared = clone(request);
    if (!prepared || typeof prepared !== 'object') return null;
    if (Array.isArray(prepared.items)) {
      if (!prepared.items.length || !prepared.items[0] || typeof prepared.items[0] !== 'object') return null;
      prepared.items[0].id = target.id;
    } else {
      prepared.id = target.id;
    }
    return prepared;
  }

  function makeEvent(name, detail) {
    if (typeof global.CustomEvent === 'function') return new global.CustomEvent(name, { bubbles: true, detail: detail });
    var document = global.document;
    if (document && document.createEvent) {
      var event = document.createEvent('CustomEvent');
      event.initCustomEvent(name, true, false, detail);
      return event;
    }
    return { type: name, bubbles: true, detail: detail };
  }

  function dispatch(target, name, detail) {
    if (target && target.dispatchEvent) target.dispatchEvent(makeEvent(name, detail));
  }

  function syncCard(form, target, variant) {
    if (!form || !form.querySelectorAll) return;
    var pack = target && target.pack ? String(target.pack) : optionValue(variant, 2);
    if (!pack) return;
    each(form.querySelectorAll('input[name="option-bundle"]'), function (input) {
      var isSelected = String(input.value) === pack;
      input.checked = isSelected;
      if (isSelected && input.setAttribute) input.setAttribute('checked', 'checked');
      else if (!isSelected && input.removeAttribute) input.removeAttribute('checked');
    });
  }

  function updatePrices(owner, target) {
    if (!owner || !owner.element || !owner.element.querySelectorAll || !target) return;
    var priceLists = owner.element.querySelectorAll('.ProductMeta__PriceList');
    var priceList = priceLists[0];
    if (priceList) {
      var compare = target.compareAtPrice !== null && target.compareAtPrice > target.price && target.compareAtText;
      var document = priceList.ownerDocument || (owner.element && owner.element.ownerDocument) || global.document;
      var priceNodes = priceList.querySelectorAll ? priceList.querySelectorAll('.ProductMeta__Price') : [];
      var mainNode = null;
      var compareNode = null;
      each(priceNodes, function (node) {
        if (node.classList && node.classList.contains('Price--compareAt')) compareNode = compareNode || node;
        else mainNode = mainNode || node;
      });
      if (!mainNode && document && document.createElement && priceList.appendChild) {
        mainNode = document.createElement('span');
        priceList.appendChild(mainNode);
      }
      if (compare && !compareNode && document && document.createElement && priceList.insertBefore) {
        compareNode = document.createElement('span');
        priceList.insertBefore(compareNode, mainNode || null);
      }
      if (mainNode) {
        mainNode.className = 'ProductMeta__Price Price ' + (compare ? 'Price--highlight ' : '') + 'Text--subdued u-h4';
        mainNode.setAttribute('data-money-convertible', '');
        mainNode.textContent = target.priceText;
      } else if (priceList.textContent !== undefined) {
        priceList.textContent = target.priceText;
      }
      if (compareNode) {
        compareNode.className = 'ProductMeta__Price Price Price--compareAt Text--subdued u-h4';
        compareNode.setAttribute('data-money-convertible', '');
        compareNode.textContent = target.compareAtText || '';
        if (compareNode.style) compareNode.style.display = compare ? '' : 'none';
      }
      var discounts = priceList.querySelectorAll ? priceList.querySelectorAll('.ProductMeta__Discount') : [];
      each(discounts, function (discount) {
        if (discount.style) discount.style.display = compare ? '' : 'none';
        if (!compare || !discount.querySelectorAll) return;
        var percentage = Math.round((target.compareAtPrice - target.price) * 100 / target.compareAtPrice);
        var percentageNodes = discount.querySelectorAll('.ProductMeta__DiscountPercentage span');
        each(percentageNodes, function (node) { node.textContent = String(percentage); });
      });
      if (priceList.style) priceList.style.display = '';
    }
    var buttonPrices = owner.element.querySelectorAll('.ProductForm__AddToCart [data-money-convertible]');
    each(buttonPrices, function (price) { price.textContent = target.priceText; });
  }

  function syncVariant(owner, form, variant) {
    var target = resolve(owner, form, variant);
    syncCard(form, target, variant);
    if (target) updatePrices(owner, target);
    dispatch(form, 'firefly:target-changed', {
      target: target,
      form: form
    });
  }

  function bind(owner, form) {
    if (!owner) return undefined;
    form = ownerForm(owner, form);
    var contract = readContract(form);
    if (!contract.present) {
      unbind(owner);
      return undefined;
    }
    var previous = getState(owner);
    if (previous && previous.form === form) {
      setFormOwner(form, owner);
      syncVariant(owner, form, owner.currentVariant);
      return true;
    }
    if (previous) unbind(owner);

    var state = {
      form: form,
      cardListeners: [],
      variantListener: null
    };
    var cards = form && form.querySelectorAll ? form.querySelectorAll('input[name="option-bundle"]') : [];
    each(cards, function (card) {
      var listener = function () {
        var pack = stringId(card.value);
        if (pack !== '1' && pack !== '2' && pack !== '4') return;
        var nativeInputs = form.querySelectorAll ? form.querySelectorAll('input[type="radio"][data-option-position="2"]') : [];
        var selected = null;
        each(nativeInputs, function (input) {
          if (!selected && String(input.value) === pack) selected = input;
        });
        if (!selected) return;
        selected.checked = true;
        dispatch(selected, 'change', undefined);
      };
      if (card.addEventListener) card.addEventListener('change', listener);
      state.cardListeners.push({ element: card, listener: listener });
    });
    state.variantListener = function (event) {
      if (event && event.target && owner.element && event.target !== owner.element) return;
      var detail = event && event.detail ? event.detail : {};
      syncVariant(owner, form, detail.variant || owner.currentVariant);
    };
    if (owner.element && owner.element.addEventListener) owner.element.addEventListener('variant:changed', state.variantListener);
    setState(owner, state);
    setFormOwner(form, owner);
    owner._lumosFireflyPurchaseBound = true;
    syncVariant(owner, form, owner.currentVariant);
    return true;
  }

  function unbind(owner) {
    var state = getState(owner);
    if (!state) {
      if (owner) owner._lumosFireflyPurchaseBound = false;
      return;
    }
    deleteFormOwner(state.form, owner);
    each(state.cardListeners, function (entry) {
      if (entry.element && entry.element.removeEventListener) entry.element.removeEventListener('change', entry.listener);
    });
    if (owner && owner.element && owner.element.removeEventListener && state.variantListener) owner.element.removeEventListener('variant:changed', state.variantListener);
    deleteState(owner);
    if (owner) owner._lumosFireflyPurchaseBound = false;
  }

  function targetForForm(form) {
    var owner = getFormOwner(form);
    if (!owner) return undefined;
    var state = getState(owner);
    if (!state || state.form !== form) {
      deleteFormOwner(form, owner);
      return undefined;
    }
    return resolve(owner, form, owner.currentVariant);
  }

  var api = {
    bind: bind,
    unbind: unbind,
    resolve: resolve,
    prepareRequest: prepareRequest,
    targetForForm: targetForForm
  };
  global.LumosFireflyPurchase = api;
  if (global.LumosCartOperations && typeof global.LumosCartOperations.refreshProductAdapters === 'function') {
    global.LumosCartOperations.refreshProductAdapters();
  }
}(window));
